import * as React from 'react'
import { SavedImage } from './useSaveImage'
import { CreateWithAiThumbnail } from './CreateWithAiThumbnail'
import { UploadImageThumbnail } from './UploadImageThumbnail'

// Define CSS styles as JavaScript objects using BEM naming convention
const customScrollbarStyles = `
    .thumbnail-grid__container::-webkit-scrollbar {
        width: 6px;
        height: 6px;
        display: block;
    }

    .thumbnail-grid__container::-webkit-scrollbar-track {
        background: transparent;
        display: block;
    }

    .thumbnail-grid__container::-webkit-scrollbar-thumb {
        background: rgba(0, 0, 0, 0.2);
        border-radius: 3px;
        transition: background 0.2s ease;
        display: block;
    }

    .thumbnail-grid__container::-webkit-scrollbar-thumb:hover {
        background: rgba(0, 0, 0, 0.3);
    }

    .thumbnail-grid__container {
        scrollbar-width: thin;
        scrollbar-color: rgba(0, 0, 0, 0.2) transparent;
        overflow-y: scroll !important;
    }
`

const animationStyles = `
    @keyframes fadeInDown {
        from {
            opacity: 0;
            transform: translateY(-20px);
        }
        to {
            opacity: 1;
            transform: translateY(0);
        }
    }

    .thumbnail-grid__item {
        animation: fadeInDown 0.3s ease-out;
        transition: all 0.3s ease-out;
    }

    .thumbnail-grid__item:not(:first-child) {
        transition-delay: 0.1s;
    }

    .thumbnail-grid__delete-icon {
        opacity: 0;
        transition: opacity 0.2s ease-out;
    }

    .thumbnail-grid__item:hover .thumbnail-grid__delete-icon {
        opacity: 1;
    }
`

interface Props {
    images: SavedImage[]
    activeImageUrl?: string
    activeImageId?: string
    onImageSelect: (image: SavedImage) => void
    onCreateNew: () => void
    onDelete?: (imageId: string) => void
    onUpload?: (file: File) => void
}

export const ThumbnailGrid = ({
    images,
    activeImageUrl,
    activeImageId,
    onImageSelect,
    onCreateNew,
    onDelete,
    onUpload,
}: Props): JSX.Element => {
    const containerRef = React.useRef<HTMLDivElement>(null)
    const [thumbnailSize, setThumbnailSize] = React.useState({ width: 0, height: 0 })

    // Calculate thumbnail size based on container width
    const calculateThumbnailSize = React.useCallback(() => {
        if (!containerRef.current) return

        const containerWidth = containerRef.current.clientWidth
        const containerPadding = 8 * 2 // Reduced container padding to 8px on each side
        const imagePadding = 2 * 2 // Reduced image padding to 2px on each side
        const borderWidth = 2 * 2 // Border width remains same (2px on each side)
        const totalHorizontalSpacing = containerPadding + imagePadding + borderWidth

        // Calculate available width for the image
        const availableWidth = containerWidth - totalHorizontalSpacing

        // Set width to fill available space
        const width = availableWidth
        // Maintain 16:9 aspect ratio for height
        const height = Math.round(width * (9/16))

        setThumbnailSize({ width, height })
    }, [])

    // Add a resize observer for more accurate size updates
    React.useEffect(() => {
        if (!containerRef.current) return

        calculateThumbnailSize()

        const resizeObserver = new ResizeObserver(() => {
            calculateThumbnailSize()
        })

        resizeObserver.observe(containerRef.current)

        return () => {
            resizeObserver.disconnect()
        }
    }, [calculateThumbnailSize])

    // Remove the window resize listener since we're using ResizeObserver
    React.useEffect(() => {
        calculateThumbnailSize()
    }, [calculateThumbnailSize])

    // Combine images, AI thumbnail, and upload thumbnail into a single array
    const allItems = React.useMemo(() => {
        // Filter images to only show the main images (1.91:1 ratio)
        // If mainDimensionId is set, use it to determine the main image
        // Otherwise, use the default image (which is already in the images array)
        const mainImages = images.map(image => {
            // If the image has dimension variants and a mainDimensionId
            if (image.dimensionVariants && image.mainDimensionId) {
                const mainVariant = image.dimensionVariants[image.mainDimensionId]
                if (mainVariant) {
                    // Return a new image object with the main variant's URL
                    return {
                        ...image,
                        url: mainVariant.url,
                        width: mainVariant.width,
                        height: mainVariant.height
                    }
                }
            }
            // If no dimension variants or mainDimensionId, return the original image
            return image
        })

        // Always include the "Create New" button and "Upload" button if onUpload is provided
        const additionalItems: ({ type: 'create-new' } | { type: 'upload' })[] = [{ type: 'create-new' }]
        if (onUpload) {
            additionalItems.push({ type: 'upload' })
        }

        return [...mainImages, ...additionalItems]
    }, [images, onUpload])

    const isCreateNew = (item: SavedImage | { type: 'create-new' } | { type: 'upload' }): item is { type: 'create-new' } => {
        return 'type' in item && item.type === 'create-new'
    }

    const isUpload = (item: SavedImage | { type: 'create-new' } | { type: 'upload' }): item is { type: 'upload' } => {
        return 'type' in item && item.type === 'upload'
    }

    // Handle delete click
    const handleDeleteClick = (e: React.MouseEvent, id: string) => {
        e.stopPropagation() // Prevent triggering the parent onClick
        onDelete?.(id)
    }

    // Fix the condition for active item
    const isActiveItem = (item: SavedImage): boolean => {
        if (activeImageId && item.id === activeImageId) {
            return true;
        }
        if (!activeImageId && activeImageUrl && item.url === activeImageUrl) {
            return true;
        }
        return false;
    }

    return (
        <>
            <style>{customScrollbarStyles}</style>
            <style>{animationStyles}</style>
            <div
                ref={containerRef}
                className="thumbnail-grid__container"
                style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '5px',
                    width: '100%',
                    height: '100%',
                    overflowY: 'scroll',
                    overflowX: 'hidden',
                    padding: '8px',
                    paddingBottom: '16px',
                    boxSizing: 'border-box',
                    position: 'relative',
                    minHeight: 0,
                    willChange: 'transform',
                    transform: 'translateZ(0)',
                }}
            >
                {allItems.map(item =>
                    isCreateNew(item) ? (
                        <CreateWithAiThumbnail
                            key="create-new"
                            onClick={onCreateNew}
                            className="thumbnail-grid__item"
                            style={{
                                width: thumbnailSize.width ? `${thumbnailSize.width}px` : 'auto',
                                height: thumbnailSize.height ? `${thumbnailSize.height}px` : 'auto',
                                marginBottom: '8px',
                                flexShrink: 0,
                            }}
                        />
                    ) : isUpload(item) ? (
                        <UploadImageThumbnail
                            key="upload"
                            onUpload={onUpload!}
                            className="thumbnail-grid__item"
                            style={{
                                width: thumbnailSize.width ? `${thumbnailSize.width}px` : 'auto',
                                height: thumbnailSize.height ? `${thumbnailSize.height}px` : 'auto',
                                marginBottom: '8px',
                                flexShrink: 0,
                            }}
                        />
                    ) : (
                        <div
                            key={item.id}
                            className={`thumbnail-grid__item thumbnail-grid__item--image ${
                                isActiveItem(item) ? 'thumbnail-grid__item--active' : 'thumbnail-grid__item--inactive'
                            }`}
                            style={{
                                width: thumbnailSize.width ? `${thumbnailSize.width}px` : 'auto',
                                height: thumbnailSize.height ? `${thumbnailSize.height}px` : 'auto',
                                position: 'relative',
                                cursor: 'pointer',
                                border: `2px solid ${isActiveItem(item) ? '#0066FF' : 'transparent'}`,
                                borderRadius: '4px',
                                overflow: 'hidden',
                                flexShrink: 0,
                                boxSizing: 'border-box',
                                marginBottom: '8px',
                            }}
                            onClick={() => onImageSelect(item)}
                        >
                            <img
                                src={item.url}
                                alt="AI generated thumbnail"
                                style={{
                                    width: '100%',
                                    height: '100%',
                                    objectFit: 'cover',
                                    backgroundColor: '#f0f0f0',
                                }}
                            />
                            {onDelete && (
                                <div
                                    className="thumbnail-grid__delete-icon"
                                    style={{
                                        position: 'absolute',
                                        top: '8px',
                                        right: '8px',
                                        width: '24px',
                                        height: '24px',
                                        backgroundColor: 'rgba(0, 0, 0, 0.5)',
                                        borderRadius: '50%',
                                        display: 'flex',
                                        justifyContent: 'center',
                                        alignItems: 'center',
                                        cursor: 'pointer',
                                        zIndex: 1,
                                    }}
                                    onClick={(e) => handleDeleteClick(e, item.id)}
                                >
                                    <svg
                                        width="14"
                                        height="14"
                                        viewBox="0 0 14 14"
                                        fill="none"
                                        xmlns="http://www.w3.org/2000/svg"
                                    >
                                        <path
                                            d="M1 1L13 13M1 13L13 1"
                                            stroke="white"
                                            strokeWidth="2"
                                            strokeLinecap="round"
                                        />
                                    </svg>
                                </div>
                            )}
                        </div>
                    )
                )}
            </div>
        </>
    )
}
